import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Avatar, Paper, Rating } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send } from 'lucide-react';

interface FeedbackPanelProps {
  proposalId: string | null;
}

const FeedbackItem = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// Mock feedback data - in a real app, this would come from an API
const mockFeedback = [
  {
    id: '1',
    user: {
      id: '101',
      name: 'Jane Smith',
      avatar: '/images/leaf.png',
      role: 'Problem Creator'
    },
    content: 'This solution addresses the core of our problem with customer service response times. I particularly like the AI-driven approach.',
    rating: 4,
    timestamp: '2023-05-15T14:30:00Z'
  },
  {
    id: '2',
    user: {
      id: '102',
      name: 'AI Assistant',
      avatar: '/images/bot.png',
      role: 'Solution Provider'
    },
    content: "Thank you for your feedback. I've developed this solution based on best practices in AI customer service systems. I can provide more details on implementation if needed.",
    rating: null,
    timestamp: '2023-05-15T15:45:00Z'
  }
];

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ proposalId }) => {
  const [feedback, setFeedback] = useState<string>('');
  const [rating, setRating] = useState<number | null>(null);
  const [feedbackItems, setFeedbackItems] = useState(mockFeedback);

  const handleSubmitFeedback = () => {
    if (!feedback.trim()) return;
    
    const newFeedback = {
      id: Math.random().toString(),
      user: {
        id: 'current-user',
        name: 'Current User',
        avatar: '/images/leaf.png',
        role: 'You'
      },
      content: feedback,
      rating: rating,
      timestamp: new Date().toISOString()
    };
    
    setFeedbackItems([...feedbackItems, newFeedback]);
    setFeedback('');
    setRating(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
        Solution Feedback
      </Typography>
      
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
        Provide feedback on the proposed solution. Your comments will help improve the final implementation.
      </Typography>
      
      {/* Feedback List */}
      <Box sx={{ mb: 3, maxHeight: 300, overflowY: 'auto' }}>
        {feedbackItems.map((item) => (
          <FeedbackItem key={item.id}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <Avatar src={item.user.avatar} sx={{ width: 32, height: 32, mr: 1.5 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                    {item.user.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 1 }}>
                    {item.user.role}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {formatDate(item.timestamp)}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 6, mb: 1 }}>
              {item.content}
            </Typography>
            
            {item.rating !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 6 }}>
                <Rating value={item.rating} readOnly size="small" />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 1 }}>
                  {item.rating}/5
                </Typography>
              </Box>
            )}
          </FeedbackItem>
        ))}
      </Box>
      
      {/* New Feedback Input */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
          Rate this solution:
        </Typography>
        <Rating
          value={rating}
          onChange={(event, newValue) => {
            setRating(newValue);
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Type your feedback here..."
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        sx={{
          mb: 2,
          textarea: { color: '#fff' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(138, 43, 226, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(138, 43, 226, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#8a2be2',
            },
          },
        }}
      />
      
      <Button
        variant="contained"
        endIcon={<Send size={16} />}
        onClick={handleSubmitFeedback}
        disabled={!feedback.trim()}
        sx={{
          background: 'linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)',
          color: '#fff',
          '&:hover': {
            background: 'linear-gradient(45deg, #9932CC 30%, #8A2BE2 90%)',
          },
          '&.Mui-disabled': {
            background: 'rgba(138, 43, 226, 0.3)',
            color: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        Submit Feedback
      </Button>
    </Box>
  );
};

export default FeedbackPanel; 